import { useState, useRef, useEffect, useMemo } from "react";
import { Table as TableType, TableWeekdayAvailability } from "@/types/table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreHorizontal, Minus, Plus, Calendar, Copy, Trash2, ArrowUpDown, ArrowUp, ArrowDown, MapPin, Baby, Sun, GripVertical } from "lucide-react";
import { BookableZone } from "@/hooks/useBookableZones";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type SortField = 'name' | 'capacity' | 'zone' | null;
type SortDirection = 'asc' | 'desc';

export interface TableListViewProps {
  tables: TableType[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  onUpdate: (tableId: string, updates: Partial<TableType>) => Promise<void>;
  onToggleActive: (id: string) => void;
  onManageAvailability: (table: TableType) => void;
  onDelete: (table: TableType) => void;
  onDuplicate: (table: TableType) => void;
  // Controlled sorting props
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField, direction: SortDirection) => void;
  // Bookable zone assignment props (drives reservation filtering)
  zones?: BookableZone[];
  onAssignZone?: (tableId: string, zoneId: string | null) => Promise<void>;
  // Children & stroller column visibility
  showChildrenColumn?: boolean;
  showStrollerColumn?: boolean;
  showTerraceColumn?: boolean;
  // Priority column visibility
  showPriorityColumn?: boolean;
  // Drag reorder for priorities
  onReorderPriorities?: (orderedIds: string[]) => Promise<void>;
}

interface EditingState {
  tableId: string | null;
  field: 'name' | 'capacity' | null;
}

// Sortable row component
function SortableTableRow({
  id,
  isDragEnabled,
  children,
  className,
}: {
  id: string;
  isDragEnabled: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isDragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={className}>
      {isDragEnabled && (
        <TableCell className="w-[40px] px-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground touch-none"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </TableCell>
      )}
      {children}
    </TableRow>
  );
}

export function TableListView({
  tables,
  weekdayAvailabilities,
  onUpdate,
  onToggleActive,
  onManageAvailability,
  onDelete,
  onDuplicate,
  sortField,
  sortDirection,
  onSortChange,
  zones = [],
  onAssignZone,
  showChildrenColumn = false,
  showStrollerColumn = false,
  showTerraceColumn = false,
  showPriorityColumn = false,
  onReorderPriorities,
}: TableListViewProps) {
  const [editing, setEditing] = useState<EditingState>({ tableId: null, field: null });
  const [editValue, setEditValue] = useState<string>("");
  const [saving, setSaving] = useState<string | null>(null);
  const [savingZone, setSavingZone] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDragEnabled = showPriorityColumn && sortField === null && !!onReorderPriorities;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Helper to get bookable zone for a table (sourced from tables.zone_id)
  const getTableZone = (tableId: string): BookableZone | null => {
    const t = tables.find((x) => x.id === tableId);
    if (!t?.zoneId) return null;
    return zones.find((z) => z.id === t.zoneId) || null;
  };

  // Handler for zone assignment
  const handleZoneChange = async (tableId: string, zoneId: string) => {
    if (!onAssignZone) return;
    setSavingZone(tableId);
    try {
      await onAssignZone(tableId, zoneId === 'none' ? null : zoneId);
    } finally {
      setSavingZone(null);
    }
  };

  // Natural sort function for alphanumeric strings
  const naturalCompare = (a: string, b: string): number => {
    return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' });
  };

  // Sort tables
  const sortedTables = useMemo(() => {
    if (sortField) {
      return [...tables].sort((a, b) => {
        let comparison = 0;
        if (sortField === 'name') {
          comparison = naturalCompare(a.name, b.name);
        } else if (sortField === 'capacity') {
          comparison = a.capacity - b.capacity;
        } else if (sortField === 'zone') {
          const zoneA = getTableZone(a.id)?.name || '';
          const zoneB = getTableZone(b.id)?.name || '';
          comparison = naturalCompare(zoneA, zoneB);
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    // When priority column is shown and no custom sort, sort by priority ascending
    if (showPriorityColumn) {
      return [...tables].sort((a, b) => (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0));
    }
    return tables;
  }, [tables, sortField, sortDirection, zones, showPriorityColumn]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        onSortChange(field, 'desc');
      } else {
        // Reset sort
        onSortChange(null, 'asc');
      }
    } else {
      onSortChange(field, 'asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  useEffect(() => {
    if (editing.tableId && editing.field === 'name' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = (table: TableType, field: 'name' | 'capacity') => {
    setEditing({ tableId: table.id, field });
    setEditValue(field === 'name' ? table.name : table.capacity.toString());
  };

  const cancelEditing = () => {
    setEditing({ tableId: null, field: null });
    setEditValue("");
  };

  const saveEdit = async (table: TableType) => {
    if (!editing.field) return;

    const newValue = editing.field === 'name' 
      ? editValue.trim() 
      : parseInt(editValue);

    // Validate
    if (editing.field === 'name' && !editValue.trim()) {
      cancelEditing();
      return;
    }
    if (editing.field === 'capacity' && (isNaN(newValue as number) || (newValue as number) < 1)) {
      cancelEditing();
      return;
    }

    // Check if value changed
    const currentValue = editing.field === 'name' ? table.name : table.capacity;
    if (newValue === currentValue) {
      cancelEditing();
      return;
    }

    setSaving(table.id);
    try {
      await onUpdate(table.id, { [editing.field]: newValue });
    } finally {
      setSaving(null);
      cancelEditing();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, table: TableType) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(table);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleCapacityChange = async (table: TableType, delta: number) => {
    const newCapacity = Math.max(1, table.capacity + delta);
    if (newCapacity !== table.capacity) {
      setSaving(table.id);
      try {
        await onUpdate(table.id, { capacity: newCapacity });
      } finally {
        setSaving(null);
      }
    }
  };

  const handleMinCapacityChange = async (table: TableType, delta: number) => {
    const current = table.minCapacity ?? 1;
    const newMin = Math.max(1, Math.min(table.capacity, current + delta));
    if (newMin !== current) {
      setSaving(table.id);
      try {
        await onUpdate(table.id, { minCapacity: newMin });
      } finally {
        setSaving(null);
      }
    }
  };

  const handleBookableChange = async (table: TableType, isBookable: boolean) => {
    setSaving(table.id);
    try {
      await onUpdate(table.id, { isBookable });
    } finally {
      setSaving(null);
    }
  };

  const handleAllowsChildrenChange = async (table: TableType, value: boolean) => {
    setSaving(table.id);
    try {
      await onUpdate(table.id, { allowsChildren: value });
    } finally {
      setSaving(null);
    }
  };

  const handleAllowsStrollerChange = async (table: TableType, value: boolean) => {
    setSaving(table.id);
    try {
      await onUpdate(table.id, { allowsStroller: value });
    } finally {
      setSaving(null);
    }
  };

  const handleIsTerraceChange = async (table: TableType, value: boolean) => {
    setSaving(table.id);
    try {
      await onUpdate(table.id, { isTerrace: value });
    } finally {
      setSaving(null);
    }
  };

  const handlePriorityChange = async (table: TableType, delta: number) => {
    const newPriority = Math.max(0, (table.assignmentPriority ?? 0) + delta);
    if (newPriority !== (table.assignmentPriority ?? 0)) {
      setSaving(table.id);
      try {
        await onUpdate(table.id, { assignmentPriority: newPriority });
      } finally {
        setSaving(null);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderPriorities) return;

    const oldIndex = sortedTables.findIndex(t => t.id === active.id);
    const newIndex = sortedTables.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedTables, oldIndex, newIndex);
    onReorderPriorities(reordered.map(t => t.id));
  };

  const hasZones = zones.length > 0 && onAssignZone;

  const tableContent = (
    <Table>
      <TableHeader>
        <TableRow>
          {isDragEnabled && <TableHead className="w-[40px]"></TableHead>}
          <TableHead className="w-[200px]">
            <button
              onClick={() => handleSort('name')}
              className="flex items-center hover:text-foreground transition-colors"
            >
              Nom
              {getSortIcon('name')}
            </button>
          </TableHead>
          <TableHead className="w-[140px]">
            <button
              onClick={() => handleSort('capacity')}
              className="flex items-center hover:text-foreground transition-colors"
            >
              Capacité (max)
              {getSortIcon('capacity')}
            </button>
          </TableHead>
          <TableHead className="w-[120px] text-center">Capacité (min)</TableHead>
          {hasZones && (
            <TableHead className="w-[160px]">
              <button
                onClick={() => handleSort('zone')}
                className="flex items-center hover:text-foreground transition-colors"
              >
                <MapPin className="h-4 w-4 mr-1" />
                Zone réservable
                {getSortIcon('zone')}
              </button>
            </TableHead>
          )}
          <TableHead className="w-[120px] text-center">Réservable</TableHead>
          {showChildrenColumn && (
            <TableHead className="w-[100px] text-center">
              <div className="flex items-center justify-center gap-1">
                <Baby className="h-4 w-4" />
                Enfants
              </div>
            </TableHead>
          )}
          {showStrollerColumn && (
            <TableHead className="w-[100px] text-center">Poussettes</TableHead>
          )}
          {showTerraceColumn && (
            <TableHead className="w-[100px] text-center">
              <div className="flex items-center justify-center gap-1">
                <Sun className="h-4 w-4" />
                Terrasse
              </div>
            </TableHead>
          )}
          {showPriorityColumn && (
            <TableHead className="w-[120px] text-center">Priorité</TableHead>
          )}
          <TableHead className="w-[100px] text-center">Active</TableHead>
          <TableHead className="w-[60px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedTables.map((table) => {
          const isEditingName = editing.tableId === table.id && editing.field === 'name';
          const isEditingCapacity = editing.tableId === table.id && editing.field === 'capacity';
          const isSaving = saving === table.id;

          return (
            <SortableTableRow
              key={table.id}
              id={table.id}
              isDragEnabled={isDragEnabled}
              className={isSaving ? "opacity-70" : ""}
            >
              {/* Name Column */}
              <TableCell>
                {isEditingName ? (
                  <div className="flex items-center gap-1">
                    <Input
                      ref={inputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, table)}
                      onBlur={() => saveEdit(table)}
                      className="h-8 w-full"
                      disabled={isSaving}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => startEditing(table, 'name')}
                    className="text-left w-full hover:text-primary transition-colors font-medium"
                  >
                    {table.name}
                  </button>
                )}
              </TableCell>

              {/* Capacity Column */}
              <TableCell>
                {isEditingCapacity ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, table)}
                      onBlur={() => saveEdit(table)}
                      className="h-8 w-16 text-center"
                      min={1}
                      disabled={isSaving}
                      autoFocus
                    />
                    <span className="text-muted-foreground text-sm">pers.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => handleCapacityChange(table, -1)}
                      disabled={table.capacity <= 1 || isSaving}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <button
                      onClick={() => startEditing(table, 'capacity')}
                      className="min-w-[40px] text-center font-medium hover:text-primary transition-colors"
                    >
                      {table.capacity}
                    </button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => handleCapacityChange(table, 1)}
                      disabled={isSaving}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>

              {/* Min Capacity Column */}
              <TableCell>
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => handleMinCapacityChange(table, -1)}
                    disabled={(table.minCapacity ?? 1) <= 1 || isSaving}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="min-w-[32px] text-center font-medium">
                    {table.minCapacity ?? 1}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => handleMinCapacityChange(table, 1)}
                    disabled={(table.minCapacity ?? 1) >= table.capacity || isSaving}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>

              {/* Zone Column */}
              {hasZones && (
                <TableCell>
                  <Select
                    value={getTableZone(table.id)?.id || 'none'}
                    onValueChange={(value) => handleZoneChange(table.id, value)}
                    disabled={isSaving || savingZone === table.id}
                  >
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue placeholder="Aucune zone">
                        {getTableZone(table.id) ? (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: getTableZone(table.id)?.color || '#888' }}
                            />
                            <span className="truncate">{getTableZone(table.id)?.name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Aucune zone</span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">Aucune zone</span>
                      </SelectItem>
                      {zones.map((zone) => (
                        <SelectItem key={zone.id} value={zone.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: zone.color || '#888' }}
                            />
                            <span>{zone.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              )}

              {/* Bookable Column */}
              <TableCell className="text-center">
                <Switch
                  checked={table.isBookable}
                  onCheckedChange={(checked) => handleBookableChange(table, checked)}
                  disabled={isSaving}
                />
              </TableCell>

              {/* Children Column */}
              {showChildrenColumn && (
                <TableCell className="text-center">
                  <Switch
                    checked={table.allowsChildren !== false}
                    onCheckedChange={(checked) => handleAllowsChildrenChange(table, checked)}
                    disabled={isSaving}
                  />
                </TableCell>
              )}

              {/* Stroller Column */}
              {showStrollerColumn && (
                <TableCell className="text-center">
                  <Switch
                    checked={table.allowsStroller !== false}
                    onCheckedChange={(checked) => handleAllowsStrollerChange(table, checked)}
                    disabled={isSaving}
                  />
                </TableCell>
              )}

              {/* Terrace Column */}
              {showTerraceColumn && (
                <TableCell className="text-center">
                  <Switch
                    checked={table.isTerrace === true}
                    onCheckedChange={(checked) => handleIsTerraceChange(table, checked)}
                    disabled={isSaving}
                  />
                </TableCell>
              )}

              {/* Priority Column */}
              {showPriorityColumn && (
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => handlePriorityChange(table, -1)}
                      disabled={(table.assignmentPriority ?? 0) <= 0 || isSaving}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[32px] text-center font-medium">
                      {table.assignmentPriority ?? 0}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => handlePriorityChange(table, 1)}
                      disabled={isSaving}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              )}

              {/* Active Column */}
              <TableCell className="text-center">
                <Switch
                  checked={table.isActive}
                  onCheckedChange={() => onToggleActive(table.id)}
                  disabled={isSaving}
                />
              </TableCell>

              {/* Actions Column */}
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onManageAvailability(table)}>
                      <Calendar className="mr-2 h-4 w-4" />
                      Gérer disponibilités
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDuplicate(table)}>
                      <Copy className="mr-2 h-4 w-4" />
                      Dupliquer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(table)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </SortableTableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <div className="border rounded-lg">
      {isDragEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedTables.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {tableContent}
          </SortableContext>
        </DndContext>
      ) : (
        tableContent
      )}
    </div>
  );
}
